import { useEffect, useRef, useState } from 'react';
import { apiClient, ApiRequestError } from '../api';
import safeway from '../config/safeway.json';
import type { RetailerConfig } from '../modules/Config';
import { PurchaseHistory, transformData } from '../modules/DataTransformSchema';
import { clientLogger } from '../utils/client-logger';
import type { SafewayPurchaseRow } from '../lib/connector/index.js';
import type {
  DataConnectionResult,
  PanelEligibility,
} from './useAmazonDataConnection';
import { hasExternalPanelParam } from '../config/panels';
import { useInitialParamsSnapshot } from './useInitialParams';
import {
  createSigninDoneHandler,
  getSigninError,
} from '../lib/signinPollUtils';

const POLL_INTERVAL_MS = 3000;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

type UseSafewayDataConnectionProps = {
  onSuccessConnect: (data: PurchaseHistory[], isLoadingMore?: boolean) => void;
  onConnectStart?: () => void;
  onConnectionError?: (errorDetails: string, code?: string) => void;
  onPartialSyncError?: (errorDetails: string) => void;
  onAuthComplete?: (panelEligibility?: PanelEligibility) => void;
  onGoToInitial?: () => void;
};

const retailerConfig = safeway as RetailerConfig;

const toPurchaseHistory = (rows: SafewayPurchaseRow[]): PurchaseHistory[] =>
  transformData(
    { purchases: rows },
    retailerConfig.dataTransform
  ) as unknown as PurchaseHistory[];

const useSafewayDataConnection = ({
  onSuccessConnect,
  onConnectStart,
  onConnectionError,
  onPartialSyncError,
  onAuthComplete,
  onGoToInitial,
}: UseSafewayDataConnectionProps): DataConnectionResult => {
  const { cid, rid, psid, tid, player } = useInitialParamsSnapshot();
  // Same panel params the other retailer hooks send to ensure-user / signin
  // so createUser records the panel attribution (tid, player).
  // No `token`: QuestionPro isn't wired up for Safeway (questionProTaskStatus
  // is hardcoded to 'no_token' below), and sending a token would subject it
  // to JWT verification server-side.
  const panelParams = { campaignId: cid, tid, player };

  const onSuccessConnectRef = useRef(onSuccessConnect);
  onSuccessConnectRef.current = onSuccessConnect;
  const onPartialSyncErrorRef = useRef(onPartialSyncError);
  onPartialSyncErrorRef.current = onPartialSyncError;

  const [signinId, setSigninId] = useState<string | undefined>(undefined);
  const [credentials, setCredentials] = useState<{
    email: string;
    password: string;
  }>();
  const [isFollowUpForm, setIsFollowUpForm] = useState(false);
  const hasSubmitted = useRef(false);

  const ensureSessionUser = async () => {
    await apiClient.ensureUser(panelParams);
  };

  const loadPurchaseHistory = async () => {
    try {
      await ensureSessionUser();
    } catch (error) {
      const { message, code } = getSigninError(error);
      clientLogger.info('Safeway ensure user error:', { error });
      onConnectionError?.(message, code);
      return;
    }

    void apiClient.getSafewayPurchaseHistory().catch((error) => {
      clientLogger.info('Safeway purchase history trigger error:', { error });
      const { message, code } = getSigninError(error);
      onConnectionError?.(message, code);
    });

    onSuccessConnectRef.current([], true);

    let lastSeenId = 0;
    let allRows: SafewayPurchaseRow[] = [];

    while (true) {
      try {
        const {
          syncStatus,
          purchases,
          lastSeenId: nextId,
          syncError,
        } = await apiClient.pollSafewayPurchaseSync(lastSeenId);

        if (purchases.length > 0) {
          lastSeenId = nextId;
          allRows = [...allRows, ...purchases];
          onSuccessConnectRef.current(toPurchaseHistory(allRows), true);
        }

        if (syncStatus === 'completed') {
          onSuccessConnectRef.current(toPurchaseHistory(allRows), false);
          return;
        }

        if (syncStatus === 'failed') {
          if (syncError) {
            onPartialSyncErrorRef.current?.(syncError);
          }
          onSuccessConnectRef.current(toPurchaseHistory(allRows), false);
          return;
        }
      } catch (error) {
        clientLogger.info('Safeway purchase sync poll error:', { error });
        const { message, code } = getSigninError(error);
        onConnectionError?.(message, code);
        return;
      }

      await sleep(POLL_INTERVAL_MS);
    }
  };

  const handleSigninDone = createSigninDoneHandler(
    loadPurchaseHistory,
    onAuthComplete,
    onConnectionError,
    'Safeway'
  );

  const getSigninIdAndPollSigninStatus = async () => {
    try {
      const result = await apiClient.getSafewaySignin();

      const signin = 'signin_id' in result ? result.signin_id : undefined;
      if (typeof signin === 'string' && signin) {
        setSigninId(signin);
        return;
      }

      onAuthComplete?.();
      await loadPurchaseHistory();
    } catch (error) {
      if (error instanceof ApiRequestError && error.status === 503) {
        onConnectionError?.(
          'All sync slots are currently busy. Please retry shortly.',
          'CAPACITY_FULL'
        );
        return;
      }
      const { message, code } = getSigninError(error);
      clientLogger.info('Safeway signin error:', { error });
      onConnectionError?.(message, code);
    }
  };

  const handleSubmitPreemptiveForm = ({
    email,
    password,
  }: {
    email: string;
    password: string;
  }) => {
    setCredentials({ email, password });
    onConnectStart?.();
  };

  useEffect(() => {
    if (!credentials?.email || !signinId || hasSubmitted.current) return;
    hasSubmitted.current = true;

    (async () => {
      try {
        const response = await apiClient.signin(signinId, credentials);

        if (response.type === 'NEED_SIGNIN') {
          setIsFollowUpForm(true);
          onGoToInitial?.();
          return;
        }

        if (response.type === 'ERROR') {
          handleSigninDone('ERROR', response.error);
          return;
        }

        handleSigninDone('SUCCESS', undefined, response.panelEligibility);
      } catch (error) {
        const { message, code } = getSigninError(error);
        clientLogger.info('Safeway submit error:', { error });
        onConnectionError?.(message, code);
      }
    })();
    // oxlint-disable-next-line react/exhaustive-deps
  }, [credentials, signinId]);

  return {
    getSigninIdAndPollSigninStatus,
    signinId,
    selectedRegion: 'US',
    setSelectedRegion: () => {},
    handleSubmitPreemptiveForm,
    handleSigninDone,
    isFollowUpForm,
    questionProTaskStatus: 'no_token',
    hasExternalPanel: hasExternalPanelParam({ rid, psid, tid }),
    hasPrefetchedCredentials: false,
    shouldRedirectToExternal: hasExternalPanelParam({ rid, psid, tid }),
  };
};

export default useSafewayDataConnection;
